import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { connect } from 'react-redux';
import { getContactStack } from 'features/contact/actions';
import {
  getContactDetailStack,
  getHelpArticles,
  getHelpCategories
} from 'features/contact/detail/actions';
import { setTopNavColorDark } from 'features/navigation/header/actions';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';

import styled from 'styled-components';
import { palette } from 'theme';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';

import Container from 'components/Container';
import ContentTypography from 'components/ContentTypography';
import HubspotForm from 'components/HubspotForm';
import MetaTags from 'components/MetaTags';
import PaddedDivider from 'components/PaddedDivider';
import { PageHeaderGrid, PageHeaderText } from 'components/PageHeader';
import { SectionGrid } from 'components/Section';
import SwiftypeTags from 'components/SwiftypeTags';
import { LargeSelect } from 'components/Forms';

import SVG from 'react-inlinesvg';
import ArrowRightIcon from 'mdi-material-ui/ArrowRight';

const IconSVG = styled(SVG)`
  svg {
    * {
      fill: ${palette.primary.main};
    }
  }
  margin-right: 10px;
  width: 24px;
  height: 24px;
`;

const CardBody = styled(Typography)`
  margin-top: 20px;
`;

const CTALink = styled(Button)`
  margin-left: -8px;
`;

const OptionsSectionGrid = styled(SectionGrid)`
  padding-top: 0;
`;

const SidebarItem = styled.div`
  margin-bottom: 40px;
`;

const ContactDetail = ({
  main,
  detail,
  match,
  helpArticles,
  helpCategories,
  setTopNavColorDark,
  getContactDetailStack,
  getContactStack,
  getHelpCategories,
  getHelpArticles
}) => {
  let history = useHistory();
  const [category, setCategory] = useState('');
  const [product, setProduct] = useState('');
  const [showForm, setShowForm] = useState(detail.helpful_form ? false : true);

  useEffect(() => {
    setTopNavColorDark();
    getContactDetailStack(match.url);
    getContactStack();
    getHelpCategories();
    history.replace({ search: null });
  }, [
    getContactDetailStack,
    getContactStack,
    getHelpCategories,
    history,
    match.url,
    setTopNavColorDark
  ]);

  useEffect(() => {
    setShowForm(detail.helpful_form ? false : true);
  }, [detail.helpful_form]);

  const handleCategoryChange = event => {
    setCategory(event.target.value);
    if (event.target.value === 'other') {
      handleShowForm();
    } else {
      getHelpArticles({
        filter: { categories: { $in_query: { slug: event.target.value } } }
      });
    }
    setProduct('');
  };

  const handleProductChange = event => {
    setProduct(event.target.value);
    if (event.target.value !== 'other' && event.target.value !== '') {
      getHelpArticles({
        filter: {
          categories: { $in_query: { slug: category } },
          // eslint-disable-next-line
          ['categories_tags.product']: {
            $in_query: { slug: event.target.value },
            _content_type_uid: 'product'
          }
        }
      });
    }
  };

  const handleShowForm = () => {
    const selectedProduct = _.find(products, ['slug', product]);
    history.replace({
      search:
        '?' +
        queryString.stringify(
          {
            product_family: selectedProduct
              ? `${selectedProduct.family?.[0]?.name} - ${selectedProduct.name}`
              : null,
            ticket_type:
              _.find(helpCategories, ['slug', category])?.title || null
          },
          { skipNull: true }
        )
    });

    setShowForm(true);
  };

  const products = useMemo(
    () =>
      _.sortBy(
        _.uniqBy(_.flatMap(helpArticles, 'categories_tags.product'), 'uid'),
        ['family[0].name']
      ),
    [helpArticles]
  );

  return (
    <Fragment>
      <MetaTags stack={detail} />
      <SwiftypeTags
        stack={detail}
        title={detail.headline}
        excerpt={detail.description}
      />
      <Container>
        <PageHeaderGrid container>
          <Grid item xs={12} md={8}>
            <PageHeaderText
              heading={detail.headline}
              subheading={detail.description}
            />
          </Grid>
        </PageHeaderGrid>
        <OptionsSectionGrid container spacing={40} justify="space-between">
          <Grid item xs={12} md={8} lg={7}>
            {detail.form_id && (
              <Card elevation={10}>
                <CardContent>
                  {!showForm && (
                    <>
                      <FormControl margin="normal" fullWidth>
                        <InputLabel>
                          What are you contacting us regarding?
                        </InputLabel>
                        <LargeSelect
                          value={category}
                          onChange={handleCategoryChange}
                        >
                          {helpCategories.map(category => (
                            <MenuItem key={category.id} value={category.slug}>
                              {category.title}
                            </MenuItem>
                          ))}
                          <MenuItem value="other">Other</MenuItem>
                        </LargeSelect>
                      </FormControl>
                      {category && (
                        <FormControl margin="normal" fullWidth>
                          <InputLabel>Please select a product</InputLabel>
                          <LargeSelect
                            value={product}
                            onChange={handleProductChange}
                          >
                            {products.map(product => (
                              <MenuItem key={product.id} value={product.slug}>
                                {product.family?.[0]?.name} - {product.name}
                              </MenuItem>
                            ))}
                            <MenuItem value="other">Other/NA</MenuItem>
                          </LargeSelect>
                        </FormControl>
                      )}
                      {helpArticles.length > 0 && (
                        <>
                          <PaddedDivider />
                          <Typography variant="h6" gutterBottom>
                            Here are some articles that may help you:
                          </Typography>
                          {helpArticles.map(article => (
                            <Typography
                              variant="subtitle2"
                              component="a"
                              href={article.url}
                              target="_blank"
                              gutterBottom
                            >
                              {article.title}
                            </Typography>
                          ))}
                          <PaddedDivider />
                          <Typography variant="h6" gutterBottom>
                            Not finding answers? We're here to help!
                          </Typography>
                          <Button
                            size="large"
                            variant="contained"
                            color="primary"
                            onClick={handleShowForm}
                          >
                            Contact Support
                          </Button>
                        </>
                      )}
                    </>
                  )}
                  {showForm && (
                    <HubspotForm
                      formId={detail.form_id}
                      useChiliPiperConcierge={detail.use_chili_piper_concierge}
                      size="large"
                    />
                  )}
                </CardContent>
              </Card>
            )}
            {detail.content && (
              <ContentTypography
                variant="body2"
                dangerouslySetInnerHTML={{ __html: detail.content }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {main.benefits.map((benefit, idx) => (
              <div key={idx.toString()}>
                {benefit.hide_on[0] !== detail.uid && (
                  <SidebarItem>
                    <Typography variant="h5">{benefit.headline}</Typography>
                    <CardBody>{benefit.body}</CardBody>
                    <CTALink color="primary" href={benefit.cta_url}>
                      {benefit.cta_label} <ArrowRightIcon />
                    </CTALink>
                    <PaddedDivider />
                  </SidebarItem>
                )}
              </div>
            ))}
            {main.ctas.map((cta, idx) => (
              <SidebarItem key={idx.toString()}>
                <Typography variant="h5">{cta.title}</Typography>
                <CTALink href={cta.cta_url} color="primary">
                  {cta.file && <IconSVG src={cta.file.url} />}
                  {cta.cta_label}
                </CTALink>
              </SidebarItem>
            ))}
          </Grid>
        </OptionsSectionGrid>
      </Container>
    </Fragment>
  );
};

ContactDetail.propTypes = {
  detail: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    form_id: PropTypes.string.isRequired
  }),
  main: PropTypes.shape({
    benefits: PropTypes.array.isRequired,
    ctas: PropTypes.array.isRequired
  })
};

export default connect(
  state => ({
    detail: state.contact_detail.stack,
    main: state.contact.stack,
    helpArticles: state.contact_detail.helpArticles,
    helpCategories: state.contact_detail.helpCategories
  }),
  {
    getContactDetailStack,
    getContactStack,
    getHelpCategories,
    getHelpArticles,
    setTopNavColorDark
  }
)(ContactDetail);
